import 'styled-components/macro';
import React, { FC, useState, useEffect, FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { ErrorState, Typography, Button, useTheme, TextField } from '@harver/shared-ui';

import { useContent } from '../../branding/content.context';
import { getInterviewDetails } from '../shared/services';
import { reschedule } from './service';
import { INTERVIEW_STATUS, RESCHEDUlING_ERRORS, SETTINGS_ERRORS } from '../shared/domains';
import { CancelledInterview as Cancelled } from '../shared/views/CancelledInterview';
import { AlreadyUnavailableInterview } from '../shared/views/AlreadyUnavailableInterview';
import { CompletedInterview } from '../shared/views/CompletedInterview';
import { NoAvailableSlots } from './NoAvailableSlots';
import { ContentCard } from '../../components/ContentCard';
import { AutocompleteField } from '../../components/SharedStyledElements';
import { SettingDisabled as Disabled } from '../shared/views/SettingDisabled';
import CookieBanner from '../../components/CookieBanner';

export const Rescheduling: FC = () => {
    const theme = useTheme();
    const { rescheduling: LANG } = useContent();

    const params = new URLSearchParams(useLocation().search);
    const calendarEventId = params.get('ref') as string;

    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [interviewStatus, setInterviewStatus] = useState<INTERVIEW_STATUS | ''>('');
    const [areRecruitersAvailable, setAreRecruitersAvailable] = useState<false | null>(null);
    const [selectedReason, setSelectedReason] = useState(LANG.reasons[0]);
    const [interviewDisabled, setInterviewDisabled] = useState(false);

    const isSubmitDisabled = isLoading || !selectedReason;

    useEffect(() => {
        getInterviewDetails(calendarEventId)
            .then((resp) => {
                setInterviewStatus(resp.status);
            })
            .catch(() => {
                setError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [calendarEventId]);

    const handleOnReasonSelect = (_, selectedOption: string): void => {
        setSelectedReason(selectedOption);
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void | boolean> => {
        e.preventDefault();

        if (isLoading) return false;

        try {
            setLoading(true);

            const rescheduledLink = await reschedule(calendarEventId, selectedReason);
            if (rescheduledLink) {
                window.location.replace(rescheduledLink);
                return;
            }
        } catch (err) {
            const errorCode = err?.response?.data?.context?.code;
            if (errorCode === RESCHEDUlING_ERRORS.noAvailableSlots) {
                setAreRecruitersAvailable(false);
                setLoading(false);
                return;
            }

            if (errorCode === SETTINGS_ERRORS.settingDisabled) {
                setInterviewDisabled(true);
                setLoading(false);
                return;
            }

            setError(true);
            setLoading(false);
        }
    };

    if (interviewStatus === INTERVIEW_STATUS.cancelled) {
        return <Cancelled />;
    }

    if (interviewStatus === INTERVIEW_STATUS.unavailable) {
        return <AlreadyUnavailableInterview />;
    }

    if (interviewStatus === INTERVIEW_STATUS.completed) {
        return <CompletedInterview />;
    }

    if (areRecruitersAvailable === false) {
        return <NoAvailableSlots />;
    }

    if (interviewDisabled) {
        return <Disabled />;
    }

    if (error) {
        document.title = 'Something went wrong';
        return <ErrorState testId="rescheduling.errorScreen" onReloadClick={() => window.location.reload()} />;
    }

    return (
        <ContentCard>
            <form
                onSubmit={handleSubmit}
                name="reschedulingForm"
                data-testid="rescheduling-form"
                css={{
                    margin: `${theme.sizes.xxLarge} 0`,
                }}
            >
                <Typography
                    css={{
                        textAlign: 'center',
                        marginBottom: theme.sizes.medium,
                    }}
                    testId="title"
                    variant="h2"
                >
                    {LANG.title}
                </Typography>

                <Typography
                    css={{
                        textAlign: 'center',
                        marginBottom: theme.sizes.large,
                    }}
                    variant="body2"
                >
                    {LANG.message}
                </Typography>

                <AutocompleteField
                    css={{ width: '100%', marginBottom: theme.sizes.large }}
                    value={selectedReason}
                    options={LANG.reasons}
                    getOptionLabel={(option: string) => option}
                    isDisabled={isLoading}
                    renderOption={(option: string) => (
                        <span data-testid={`rescheduling.option.${option}`}>{option}</span>
                    )}
                    onChange={handleOnReasonSelect}
                    renderInput={(params: unknown): JSX.Element => (
                        <TextField
                            {...params}
                            variant="filled"
                            label={LANG.reasonSelectionLabel}
                            testId="select-rescheduling-reason"
                        />
                    )}
                />

                <div
                    css={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Button
                        type="submit"
                        color="primary"
                        disabled={isSubmitDisabled}
                        testId="reschedule-interview-button"
                    >
                        {LANG.title}
                    </Button>
                </div>
            </form>
            <CookieBanner />
        </ContentCard>
    );
};
